import React, { useMemo } from 'react'

type WaveCircleProps = {
  valueDeci: number | null
  size?: number
  phase?: 'Running' | 'Stable' | 'Failed'
  label?: string
}

// Simple animated wave fill inside a circular mask using SVG.
// The wave moves horizontally; the vertical level reflects the current pH value.
const WaveCircle: React.FC<WaveCircleProps> = ({ valueDeci, size = 144, phase = 'Running', label }) => {
  const width = size
  const height = size
  const display = useMemo(() => (valueDeci != null ? (valueDeci / 10).toFixed(2) : '-'), [valueDeci])
  // Map deci-pH (0–140) to fill percentage
  const percent = useMemo(() => {
    const v = valueDeci ?? 0
    const clamped = Math.max(0, Math.min(140, v))
    return clamped / 140
  }, [valueDeci])

  const level = Math.round(height * percent)
  const waveColor = phase === 'Stable' ? '#14b8a6' : phase === 'Failed' ? '#ef4444' : '#06b6d4'
  const bgColor = '#0f172a' // dark slate background inside circle for contrast
  const strokeColor = waveColor

  // Build a sine wave path spanning double width for seamless animation
  const buildWavePath = (w: number, h: number, amplitude: number, wavelength: number, phaseShift: number) => {
    const points: string[] = []
    const totalW = w * 2
    for (let x = 0; x <= totalW; x += 2) {
      const y = amplitude * Math.sin((2 * Math.PI * x) / wavelength + phaseShift)
      points.push(`${x} ${Math.max(0, Math.min(h, h / 2 - y))}`)
    }
    const head = `M 0 ${h}`
    const wave = `L ${points.join(' L ')}`
    const tail = `L ${totalW} ${h} L 0 ${h} Z`
    return `${head} ${wave} ${tail}`
  }

  const wavePath1 = buildWavePath(width, height, 8, 40, 0)
  const wavePath2 = buildWavePath(width, height, 6, 28, Math.PI / 2)

  return (
    <div style={{ width, height }} className="relative">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <clipPath id="clipCircle">
            <circle cx={width / 2} cy={height / 2} r={Math.min(width, height) / 2 - 4} />
          </clipPath>
        </defs>

        <style>
          {`
          .wave-move { animation: waveMove 4s linear infinite; }
          @keyframes waveMove { from { transform: translateX(0px); } to { transform: translateX(-${width}px); } }
          `}
        </style>

        {/* Circle border */}
        <circle cx={width / 2} cy={height / 2} r={Math.min(width, height) / 2 - 4} fill={bgColor} stroke={strokeColor} strokeWidth={4} />

        {/* Waves clipped to circle */}
        <g clipPath="url(#clipCircle)">
          {/* Vertical level positioning */}
          <g transform={`translate(0, ${height - level})`}>
            <g className="wave-move" style={{ opacity: 0.9 }}>
              <path d={wavePath1} fill={waveColor} />
            </g>
            <g className="wave-move" style={{ opacity: 0.7 }}>
              <path d={wavePath2} fill={waveColor} />
            </g>
          </g>
        </g>

        {/* Center label */}
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#ffffff" fontSize={Math.round(width * 0.18)} fontWeight="700">
          {display}
        </text>
        {label ? (
          <text x="50%" y="66%" dominantBaseline="middle" textAnchor="middle" fill="#cbd5e1" fontSize={Math.round(width * 0.1)}>
            {label}
          </text>
        ) : null}
      </svg>
    </div>
  )
}

export default WaveCircle